import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { X, Search, Upload, Download, Trash2, FileText, Loader2, IdCard, FileSignature, User, History, ShieldCheck, Plus, CalendarPlus, Lock, LockOpen } from "lucide-react";
import ManualBookingForm from "./ManualBookingForm";

const DOC_KINDS = { carnet: "Carnet de conducir", contrato: "Contrato firmado", dni: "DNI / Pasaporte", acta: "Acta de entrega/devolución", entrega: "Foto entrega", devolucion: "Foto devolución", otro: "Otro documento" };
const DOC_COLORS = { carnet: "bg-topacio-red text-white", contrato: "bg-brand text-topacio-ink", dni: "bg-topacio-green text-white", acta: "bg-topacio-ink text-white", entrega: "bg-sky-500 text-white", devolucion: "bg-topacio-red text-white", otro: "bg-zinc-200 text-topacio-faded" };
const DEPOSIT_LABELS = { pendiente: "Pendiente", retenida: "Retenida", devuelta: "Devuelta", parcial: "Parcial (daños)" };
const DEPOSIT_COLORS = { pendiente: "bg-zinc-200 text-topacio-faded", retenida: "bg-brand text-topacio-ink", devuelta: "bg-topacio-green text-white", parcial: "bg-topacio-red text-white" };
const AUDIT_LABELS = { upload: "subió", download: "descargó", delete: "eliminó", keep: "protegió", unkeep: "desprotegió" };

// ---- Alta manual de cliente (sin necesidad de reserva) ----
function NewCustomerModal({ headers, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", dni: "", address: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/customers`, form, { headers });
      toast.success("Cliente dado de alta");
      onCreated(form.email.toLowerCase().trim());
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail === "invalid_email" ? "Email no válido" : "Error al crear el cliente");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="new-customer-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative card-brutal w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" data-lenis-prevent>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2"><Plus className="w-5 h-5 text-topacio-red" /> Nuevo cliente</h3>
          <button data-testid="new-customer-close" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input data-testid="new-customer-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Nombre completo" className="input-brutal" />
          <input data-testid="new-customer-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email (clave de la ficha)" className="input-brutal" />
          <input data-testid="new-customer-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Teléfono" className="input-brutal" />
          <input data-testid="new-customer-dni" value={form.dni} onChange={(e) => set("dni", e.target.value)} placeholder="DNI / Pasaporte" className="input-brutal" />
          <input data-testid="new-customer-address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Dirección habitual" className="input-brutal sm:col-span-2" />
          <textarea data-testid="new-customer-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notas internas (opcional)" className="input-brutal sm:col-span-2" />
        </div>
        <p className="text-[10px] text-topacio-faded font-semibold mt-3">La ficha queda creada aunque el cliente aún no tenga reservas. Desde la ficha podrás registrarle su primera reserva.</p>
        <button data-testid="new-customer-save" onClick={save} disabled={busy || !form.email.includes("@")} className="btn-primary w-full mt-4">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Crear cliente
        </button>
      </div>
    </div>
  );
}

export default function CustomersTab({ headers, role }) {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null); // email del cliente abierto
  const [newOpen, setNewOpen] = useState(false);
  const [bikes, setBikes] = useState([]);
  const canSeeDocs = ["admin", "superadmin"].includes(role);
  const canEdit = ["admin", "superadmin"].includes(role);

  const load = () => {
    axios.get(`${API}/admin/customers`, { headers, params: q ? { q } : {} })
      .then(({ data }) => setCustomers(data.customers)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    axios.get(`${API}/admin/bikes`, { headers }).then(({ data }) => setBikes(data.bikes)).catch(() => {});
  }, []);

  const saveManualBooking = async (form) => {
    await axios.post(`${API}/admin/bookings/manual`, form, { headers });
    toast.success("Reserva de tienda registrada y fechas bloqueadas");
    load();
  };

  return (
    <div data-testid="customers-tab">
      <h2 className="font-display text-xl font-bold text-topacio-ink mb-2">Clientes</h2>
      <p className="text-xs text-topacio-faded font-semibold mb-6 flex items-center gap-1.5">
        <ShieldCheck className="w-4 h-4 text-topacio-green" /> Ficha por email: historial, documentos blindados (solo admin), contratos y fianzas.
      </p>
      <div className="flex gap-2 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-topacio-faded" />
          <input data-testid="customers-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar por nombre, email o teléfono…" className="input-brutal !pl-9" />
        </div>
        <button data-testid="customers-search-btn" onClick={load} className="btn-secondary !px-5 !py-2.5 !text-xs">Buscar</button>
        {canEdit && (
          <button data-testid="new-customer-btn" onClick={() => setNewOpen(true)} className="btn-primary !px-5 !py-2.5 !text-xs">
            <Plus className="w-4 h-4" /> Nuevo cliente
          </button>
        )}
      </div>
      <div className="card-brutal overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="bg-topacio-ink text-left text-xs uppercase tracking-wider text-white">
              {["Cliente", "Contacto", "Alquileres", "Moto favorita", "Total gastado", "Último", "Docs"].map((h) => (
                <th key={h} className="px-5 py-4 font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {customers.map((c) => (
              <tr key={c.email} data-testid={`customer-row-${c.email}`} onClick={() => setOpen(c.email)}
                className="hover:bg-topacio-sand transition-colors cursor-pointer">
                <td className="px-5 py-4 font-bold text-topacio-ink">{c.name || "—"}</td>
                <td className="px-5 py-4 text-topacio-faded">{c.email}<br /><span className="text-xs">{c.phone}</span></td>
                <td className="px-5 py-4">
                  <span className="font-extrabold text-topacio-ink">{c.bookings}</span>
                  {c.bookings > 1 && <span data-testid={`customer-repeat-${c.email}`} className="badge-brutal bg-topacio-red text-white !text-[9px] ml-2">Repite</span>}
                </td>
                <td className="px-5 py-4 text-xs font-bold text-topacio-faded">{c.favorite_bike || "—"}</td>
                <td className="px-5 py-4 font-extrabold text-topacio-red">{c.spent}€</td>
                <td className="px-5 py-4 text-topacio-faded text-xs">{(c.last || "").slice(0, 10)}</td>
                <td className="px-5 py-4">
                  {c.docs > 0
                    ? <span className="badge-brutal bg-topacio-green text-white">{c.docs}</span>
                    : <span className="text-xs text-zinc-400">0</span>}
                </td>
              </tr>
            ))}
            {customers.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-topacio-faded">Sin clientes todavía — aparecen solos al registrar reservas.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {open && <CustomerDetail email={open} headers={headers} canSeeDocs={canSeeDocs} canEdit={canEdit} bikes={bikes} onSaveBooking={saveManualBooking} onClose={() => { setOpen(null); load(); }} />}
      {newOpen && <NewCustomerModal headers={headers} onClose={() => setNewOpen(false)} onCreated={(email) => { load(); setOpen(email); }} />}
    </div>
  );
}

function CustomerDetail({ email, headers, canSeeDocs, canEdit, bikes, onSaveBooking, onClose }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", address: "", dni: "", notes: "" });
  const [kind, setKind] = useState("carnet");
  const [side, setSide] = useState("front");
  const [file, setFile] = useState(null);
  const [docRef, setDocRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/customers/${encodeURIComponent(email)}`, { headers })
      .then(({ data: d }) => {
        setData(d);
        const p = d.profile || {};
        setForm({ name: p.name || "", phone: p.phone || "", address: p.address || "", dni: p.dni || "", notes: p.notes || "" });
      }).catch(() => toast.error("Error cargando la ficha"));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [email]);

  const saveProfile = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/customers/${encodeURIComponent(email)}`, form, { headers });
      toast.success("Ficha guardada");
      load();
    } catch { toast.error("Error al guardar"); } finally { setBusy(false); }
  };

  const uploadDoc = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      fd.append("booking_ref", docRef);
      if (["dni", "carnet"].includes(kind)) fd.append("side", side);
      await axios.post(`${API}/admin/customers/${encodeURIComponent(email)}/documents`, fd, { headers });
      toast.success("Documento subido y blindado");
      setFile(null);
      setDocRef("");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "too_big" ? "Archivo demasiado grande (máx. 10 MB)" : d === "bad_type" ? "Solo imágenes o PDF" : "Error al subir");
    } finally { setBusy(false); }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await axios.get(`${API}/admin/customers/${encodeURIComponent(email)}/documents/${doc.id}`,
        { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("No autorizado o documento no encontrado"); }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`¿Eliminar "${doc.filename}"? Esta acción queda registrada.`)) return;
    try {
      await axios.delete(`${API}/admin/customers/${encodeURIComponent(email)}/documents/${doc.id}`, { headers });
      toast.success("Documento eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail === "protected_doc" ? "Documento protegido: quita el candado primero" : "Error al eliminar");
    }
  };

  const toggleKeep = async (doc) => {
    await axios.patch(`${API}/admin/customers/${encodeURIComponent(email)}/documents/${doc.id}/keep`,
      { keep: !doc.keep }, { headers });
    toast.success(doc.keep ? "Protección quitada" : "Evidencia protegida: no se borrará en la purga de 90 días");
    load();
  };

  if (!data) return null;
  const bks = data.bookings || [];
  const spent = Math.round(bks.filter((b) => b.status !== "cancelled").reduce((s, b) => s + (b.total || 0), 0) * 100) / 100;
  const bikeCounts = {};
  bks.forEach((b) => { if (b.bike_name) bikeCounts[b.bike_name] = (bikeCounts[b.bike_name] || 0) + 1; });
  const favBike = Object.entries(bikeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";

  return (
    <div className="fixed inset-0 z-[90] flex items-stretch sm:items-center justify-center p-0 sm:p-4" data-testid="customer-detail-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative card-brutal !rounded-none sm:!rounded-xl w-full sm:max-w-3xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto" data-lenis-prevent>
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-topacio-ink sticky top-0 bg-white z-10">
          <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2">
            <User className="w-5 h-5 text-topacio-red" /> {data.profile.name || email}
          </h3>
          <button data-testid="customer-detail-close" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-8">
          {/* Resumen de un vistazo */}
          <section data-testid="customer-stats" className="flex flex-wrap gap-2">
            <span className="badge-brutal bg-topacio-ink text-white">{bks.length} alquiler{bks.length === 1 ? "" : "es"}</span>
            {bks.length > 1 && <span data-testid="customer-stats-repeat" className="badge-brutal bg-topacio-red text-white">Cliente repetidor</span>}
            <span data-testid="customer-stats-spent" className="badge-brutal bg-brand text-topacio-ink">{spent}€ en total</span>
            {favBike && <span data-testid="customer-stats-favbike" className="badge-brutal bg-topacio-green text-white">Moto favorita: {favBike}</span>}
            {data.precheckin && (
              <span data-testid="customer-precheckin-status" className={`badge-brutal ${data.precheckin.complete ? "bg-topacio-green text-white" : "bg-brand text-topacio-ink"}`}>
                {data.precheckin.complete ? "Check-in documental completo" : `Check-in pendiente (${4 - (data.precheckin.missing || []).length}/4 docs)`}
              </span>
            )}
          </section>

          {/* Ficha de datos */}
          <section>
            <h4 className="text-xs uppercase tracking-wider font-extrabold text-topacio-faded mb-3">Datos del cliente</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input data-testid="customer-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre completo" className="input-brutal" />
              <input data-testid="customer-field-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Teléfono" className="input-brutal" />
              <input data-testid="customer-field-dni" value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} placeholder="DNI / Pasaporte" className="input-brutal" />
              <input data-testid="customer-field-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Dirección habitual" className="input-brutal" />
              <textarea data-testid="customer-field-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Notas internas (alergias, preferencias, incidencias…)" className="input-brutal sm:col-span-2" />
            </div>
            <button data-testid="customer-save-btn" onClick={saveProfile} disabled={busy || !canEdit} className="btn-primary !px-5 !py-2.5 !text-xs mt-3">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar ficha
            </button>
          </section>

          {/* Documentos blindados */}
          {canSeeDocs && (
            <section>
              <h4 className="text-xs uppercase tracking-wider font-extrabold text-topacio-faded mb-3 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-topacio-green" /> Documentos blindados
                <span className="normal-case font-semibold text-zinc-400">(solo admin · cada acceso queda registrado)</span>
              </h4>
              <div className="flex flex-wrap gap-2 items-end mb-4 p-4 bg-topacio-sand border-2 border-dashed border-topacio-ink/30 rounded-lg">
                <div>
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded">Tipo</label>
                  <select data-testid="doc-kind-select" value={kind} onChange={(e) => setKind(e.target.value)} className="input-brutal mt-1 !w-44 !py-2.5">
                    {Object.entries(DOC_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                {["dni", "carnet"].includes(kind) && (
                  <div>
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded">Cara</label>
                    <select data-testid="doc-side-select" value={side} onChange={(e) => setSide(e.target.value)} className="input-brutal mt-1 !w-32 !py-2.5">
                      <option value="front">Frontal</option>
                      <option value="back">Trasera</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded">Reserva (opcional)</label>
                  <select data-testid="doc-ref-select" value={docRef} onChange={(e) => setDocRef(e.target.value)} className="input-brutal mt-1 !w-40 !py-2.5">
                    <option value="">—</option>
                    {bks.map((b) => <option key={b.ref} value={b.ref}>{b.ref} · {b.start_date}</option>)}
                  </select>
                </div>
                <label data-testid="doc-file-label" className="btn-secondary !px-4 !py-2.5 !text-xs cursor-pointer">
                  <Upload className="w-4 h-4" /> {file ? file.name.slice(0, 18) : "Elegir archivo"}
                  <input data-testid="doc-file-input" type="file" accept="image/*,application/pdf" className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
                <button data-testid="doc-upload-btn" onClick={uploadDoc} disabled={!file || busy} className="btn-primary !px-4 !py-2.5 !text-xs">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Subir
                </button>
              </div>
              <div className="space-y-2">
                {(data.documents || []).map((d) => (
                  <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-3 border-2 border-zinc-200 rounded-lg px-4 py-3">
                    <span className={`badge-brutal ${DOC_COLORS[d.kind]} !text-[9px]`}>{DOC_KINDS[d.kind] || d.kind}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-topacio-ink truncate flex items-center gap-1.5">
                        {d.filename}
                        {d.keep && <span data-testid={`doc-kept-${d.id}`} className="badge-brutal bg-topacio-ink text-brand !text-[8px]">Protegido</span>}
                      </div>
                      <div className="text-[10px] text-topacio-faded">{d.created_at?.slice(0, 10)} · {d.uploaded_by}{d.booking_ref ? ` · ${d.booking_ref}` : ""}{d.side ? ` · ${d.side === "front" ? "frontal" : "trasera"}` : ""}{d.angle ? ` · ${d.angle}` : ""}</div>
                    </div>
                    <button data-testid={`doc-keep-${d.id}`} onClick={() => toggleKeep(d)}
                      title={d.keep ? "Quitar protección (podrá purgarse)" : "Proteger evidencia: no se borra en la purga de 90 días"}
                      className={`w-8 h-8 border-2 border-topacio-ink rounded-lg flex items-center justify-center transition-colors ${d.keep ? "bg-topacio-ink text-brand" : "bg-white hover:bg-brand"}`}>
                      {d.keep ? <Lock className="w-3.5 h-3.5" /> : <LockOpen className="w-3.5 h-3.5" />}
                    </button>
                    <button data-testid={`doc-download-${d.id}`} onClick={() => downloadDoc(d)} title="Ver (queda registrado)"
                      className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-brand transition-colors">
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`doc-delete-${d.id}`} onClick={() => deleteDoc(d)} title="Eliminar (queda registrado)"
                      className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-topacio-red hover:text-white transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {(data.documents || []).length === 0 && (
                  <p className="text-xs text-topacio-faded font-semibold">Sin documentos. Sube el carnet, el contrato firmado o el DNI.</p>
                )}
                <p className="text-[10px] text-zinc-400 font-semibold mt-2">
                  Las fotos del protocolo de estado (entrega/devolución) se borran solas a los 90 días. Las actas PDF y lo marcado con el candado se conservan siempre.
                </p>
              </div>
              {(data.audit || []).length > 0 && (
                <p className="text-[10px] text-zinc-400 font-mono mt-3">
                  Registro: {data.audit.slice(0, 4).map((a) => `${a.user?.split("@")[0]} ${AUDIT_LABELS[a.action] || a.action} ${a.at?.slice(5, 16).replace("T", " ")}`).join(" · ")}
                </p>
              )}
            </section>
          )}

          {/* Historial de alquileres */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs uppercase tracking-wider font-extrabold text-topacio-faded flex items-center gap-2">
                <History className="w-4 h-4" /> Historial de alquileres ({bks.length})
              </h4>
              {canEdit && (
                <button data-testid="customer-new-booking-btn" onClick={() => setBookingOpen(true)} className="btn-primary !px-4 !py-2 !text-[10px]">
                  <CalendarPlus className="w-4 h-4" /> Crear reserva
                </button>
              )}
            </div>
            <div className="space-y-2">
              {bks.map((b) => (
                <div key={b.ref} data-testid={`customer-booking-${b.ref}`} className="border-2 border-zinc-200 rounded-lg px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="font-mono font-extrabold text-topacio-red">{b.ref}</span>
                  <span className="text-sm font-bold text-topacio-ink">{b.bike_name}</span>
                  <span className="text-xs text-topacio-faded">{b.start_date} → {b.end_date}</span>
                  <span className="text-sm font-extrabold text-topacio-ink">{b.total}€</span>
                  {b.hotel_name && <span className="text-[10px] font-bold text-topacio-faded">Hotel: {b.hotel_name}</span>}
                  {b.deposit_status && (
                    <span className={`badge-brutal !text-[9px] ${DEPOSIT_COLORS[b.deposit_status] || DEPOSIT_COLORS.pendiente}`}>
                      Fianza: {DEPOSIT_LABELS[b.deposit_status] || b.deposit_status}
                    </span>
                  )}
                  {b.channel && <span className="text-[10px] uppercase font-extrabold text-zinc-400">{b.channel}</span>}
                </div>
              ))}
              {bks.length === 0 && <p className="text-xs text-topacio-faded font-semibold">Sin alquileres registrados con este email.</p>}
            </div>
          </section>
        </div>
        {bookingOpen && (
          <ManualBookingForm bikes={bikes} headers={headers} initial={{ name: form.name, email, phone: form.phone }}
            onClose={() => setBookingOpen(false)}
            onSave={async (f) => { await onSaveBooking(f); load(); }} />
        )}
      </div>
    </div>
  );
}

export { DOC_KINDS, DEPOSIT_LABELS, DEPOSIT_COLORS, FileText, IdCard, FileSignature };
